import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "../../components/Layout/AdminLayout/AdminLayout";

import { LoanStatusDetails, UpdateLoanApplicationDetailsInput } from "../../interface/ILoanManagement";

import loanManagementService from "./loanManagementService";

import ApplicationForLoanForm from "../../components/LoanApplicationForms/ApplicationForLoanForm";
import DisclosureStatementLoanCreditTransactionForm from "../../components/LoanApplicationForms/DisclosureStatementLoanCreditTransactionForm";
import CertificateForm from "../../components/LoanApplicationForms/CertificateForm";
import ApproveLoanModal from "../../components/Modal/ApproveLoanModal";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import ConfirmModal2 from "../../components/Modal/ConfirmModal2";
import MessageModal from "../../components/Modal/MessageModal";

import warningIcon from '../../assets/warning-icon.png';
import successIcon from '../../assets/success-icon.png';
import questionIcon from '../../assets/question-icon.png';


function PreviewLoanApplicationForm() {

    const navigate = useNavigate();

    const [loanStatusDetails, setLoanStatusDetails] = useState<LoanStatusDetails | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDisclosureOpen, setIsDisclosureOpen] = useState(false);
    const [isCertificationOpen, setIsCertificationOpen] = useState(false);
    const [gmSignature, setGmSignature] = useState<string>('');
    const [gmFullname, setGmFullname] = useState<string>('');
    const [cc1Signature, setCc1Signature] = useState<string>('');
    const [cc1Fullname, setCc1Fullname] = useState<string>('');
    const [cc2Signature, setCc2Signature] = useState<string>('');
    const [cc2Fullname, setCc2Fullname] = useState<string>('');
    const [cc3Signature, setCc3Signature] = useState<string>('');
    const [cc3Fullname, setCc3Fullname] = useState<string>('');
    const [repSignature, setRepSignature] = useState<string>('');
    const [repFullname, setRepFullname] = useState<string>('');
    const [repPosition, setRepPosition] = useState<string>('');
    const [attestedSignature, setAttestedSignature] = useState<string>('');
    const [attestedFullname, setAttestedFullname] = useState<string>('');
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [isDeclineConfirmOpen, setIsDeclineConfirmOpen] = useState(false);
    const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
    const [pendingApproveLoanDate, setPendingApproveLoanDate] = useState<Date | null>(null);
    const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
    const [isDeclineSuccessOpen, setIsDeclineSuccessOpen] = useState(false);
    const [isApproveSuccessOpen, setIsApproveSuccessOpen] = useState(false);
    const [isSaveSuccessOpen, setIsSaveSuccessOpen] = useState(false);
    const [loanStatus, setLoanStatus] = useState<number | null>(null);
    const { loanId } = useParams<{ loanId: string }>();

    async function loadLoanStatusDetails() {
        try {
            var axiosResponse = await loanManagementService.getLoanStatusDetails(Number(loanId));
            const details = axiosResponse.data.result ?? null;
            setLoanStatusDetails(details);
            setGmSignature(details?.gmSignature ?? '');
            setGmFullname(details?.gmFullname ?? '');
            setCc1Signature(details?.creditCommitteeSignature1 ?? '');
            setCc1Fullname(details?.creditCommitteeFullname1 ?? '');
            setCc2Signature(details?.creditCommitteeSignature2 ?? '');
            setCc2Fullname(details?.creditCommitteeFullname2 ?? '');
            setCc3Signature(details?.creditCommitteeSignature3 ?? '');
            setCc3Fullname(details?.creditCommitteeFullname3 ?? '');
            setRepSignature(details?.representativeSignature ?? '');
            setRepFullname(details?.representativeFullname ?? '');
            setRepPosition(details?.representativePosition ?? '');
            setAttestedSignature(details?.attestedBySignature ?? '');
            setAttestedFullname(details?.attestedByFullname ?? '');
            setLoanStatus(details?.loanStatus ?? null);
        }
        catch (err) {
            console.log('loadLoanStatusDetails err: ', err);
        }
    }

    async function handleSaveAsDraft() {
        try {
            document.querySelector('.loader-container')?.classList.add('loader');
            const input: UpdateLoanApplicationDetailsInput = {
                gmSignature: gmSignature ?? '',
                gmFullname: gmFullname ?? '',
                creditCommitteeSignature1: cc1Signature ?? '',
                creditCommitteeFullname1: cc1Fullname ?? '',
                creditCommitteeSignature2: cc2Signature ?? '',
                creditCommitteeFullname2: cc2Fullname ?? '',
                creditCommitteeSignature3: cc3Signature ?? '',
                creditCommitteeFullname3: cc3Fullname ?? '',
                disclosureReportSignature: loanStatusDetails?.disclosureReportSignature ?? '',
                disclosureReportFullname: loanStatusDetails?.disclosureReportFullname ?? '',
                deptHeadSignature: loanStatusDetails?.deptHeadSignature ?? '',
                deptHeadFullname: loanStatusDetails?.deptHeadFullname ?? '',
                representativeSignature: repSignature ?? '',
                representativeFullname: repFullname ?? '',
                representativePosition: repPosition ?? '',
                attestedBySignature: attestedSignature ?? '',
                attestedByFullname: attestedFullname ?? '',
            };
            await loanManagementService.updateLoanApplicationDetails(Number(loanId), input);
            setIsSaveSuccessOpen(true);
        }
        catch (err) {
            console.log('handleSaveAsDraft err: ', err);
        }
        finally {
            document.querySelector('.loader-container')?.classList.remove('loader');
        }
    }

    async function handleDecline() {
        try {
            document.querySelector('.loader-container')?.classList.add('loader');
            await loanManagementService.declineLoanApplication(Number(loanId));
            setIsDeclineSuccessOpen(true);
        }
        catch (err) {
            console.log('declineLoanApplication err: ', err);
        }
        finally {
            document.querySelector('.loader-container')?.classList.remove('loader');
        }
    }

    useEffect(function () {
        (async () => {
            document.querySelector('.loader-container')?.classList.add('loader');
            await loadLoanStatusDetails();
            document.querySelector('.loader-container')?.classList.remove('loader');
        })();
    }, []);

    return (
        <>
        <Layout>

            <div className="title-container">
                <div>
                    <div className="dv-title">
                        <h1>Loan Application Form</h1>
                    </div>
                </div>
                <div className="title-btn-container vertical-flex-bottom">
                    <button
                        type="button"
                        id="btnBack"
                        onClick={() => { navigate(`/Admin/LoanStatusDetails/${loanId}`); }}
                        className="btn-white-green-2 center-middle"
                    >
                        Back
                    </button>
                </div>
            </div>

            <br />

            <div className="accordion-item" style={{ marginBottom: '16px' }}>
                <div className={`accordion-header ${isFormOpen ? 'active' : ''}`} onClick={() => setIsFormOpen(prev => !prev)}>
                    <span>Application For Loan</span>
                </div>
                <div className={`accordion-body ${isFormOpen ? 'open' : ''}`}>
                    <ApplicationForLoanForm
                        loanId={loanId}
                        loanStatusDetails={loanStatusDetails}
                        onGmSignatureChange={(sig, name) => { setGmSignature(sig); setGmFullname(name); }}
                        onCreditCommittee1Change={(sig, name) => { setCc1Signature(sig); setCc1Fullname(name); }}
                        onCreditCommittee2Change={(sig, name) => { setCc2Signature(sig); setCc2Fullname(name); }}
                        onCreditCommittee3Change={(sig, name) => { setCc3Signature(sig); setCc3Fullname(name); }}
                    />
                </div>
            </div>

            <div className="accordion-item" style={{ marginBottom: '16px' }}>
                <div className={`accordion-header ${isDisclosureOpen ? 'active' : ''}`} onClick={() => setIsDisclosureOpen(prev => !prev)}>
                    <span>Disclosure Statement of Loan or Credit Transaction</span>
                </div>
                <div className={`accordion-body ${isDisclosureOpen ? 'open' : ''}`}>
                    <DisclosureStatementLoanCreditTransactionForm
                        loanId={loanId}
                        loanStatusDetails={loanStatusDetails}
                        onRepresentativeChange={(sig, name) => { setRepSignature(sig); setRepFullname(name); }}
                        onRepresentativePositionChange={(pos) => setRepPosition(pos)}
                    />
                </div>
            </div>

            <div className="accordion-item" style={{ marginBottom: '16px' }}>
                <div className={`accordion-header ${isCertificationOpen ? 'active' : ''}`} onClick={() => setIsCertificationOpen(prev => !prev)}>
                    <span>Certification</span>
                </div>
                <div className={`accordion-body ${isCertificationOpen ? 'open' : ''}`}>
                    <CertificateForm
                        loanId={loanId}
                        loanStatusDetails={loanStatusDetails}
                        onAttestedByChange={(sig, name) => { setAttestedSignature(sig); setAttestedFullname(name); }}
                    />
                </div>
            </div>

            <div className="accordion-item">
                <div className="center-middle" style={{ padding: '16px', gap: '12px' }}>
                    <button
                        type="button"
                        id="btnDecline"
                        className="btn-red-4 center-middle"
                        disabled={loanStatus !== 2}
                        onClick={() => setIsDeclineConfirmOpen(true)}
                    >
                        Decline
                    </button>
                    <button
                        type="button"
                        id="btnApproveApplication"
                        className="btn-green-4 center-middle"
                        disabled={loanStatus !== 2 || !gmSignature || !cc1Signature || !cc2Signature || !cc3Signature || !repSignature || !attestedSignature}
                        onClick={() => setIsApproveModalOpen(true)}
                    >
                        Approve
                    </button>
                    <button
                        type="button"
                        id="btnSaveAsDraft"
                        className="btn-green-4 center-middle"
                        disabled={loanStatus !== 2}
                        onClick={() => setIsSaveConfirmOpen(true)}
                    >
                        Save as Draft
                    </button>
                </div>
            </div>

        </Layout>

        <MessageModal
            icon={successIcon}
            message="Declined Successfully!"
            isOpen={isDeclineSuccessOpen}
            onClose={() => { setIsDeclineSuccessOpen(false); window.location.reload(); }}
        />
        <MessageModal
            icon={successIcon}
            message="Approve Loan Application Successfully!"
            isOpen={isApproveSuccessOpen}
            onClose={() => { setIsApproveSuccessOpen(false); window.location.reload(); }}
        />
        <MessageModal
            icon={successIcon}
            message="Save Draft succesful"
            isOpen={isSaveSuccessOpen}
            onClose={() => { setIsSaveSuccessOpen(false); window.location.reload(); }}
        />
        <ConfirmModal
            icon={warningIcon}
            message="Are you sure you want to approve this loan application?"
            buttonYesText="Approve"
            isOpen={isApproveConfirmOpen}
            onYes={async () => {
                setIsApproveConfirmOpen(false);
                try {
                    document.querySelector('.loader-container')?.classList.add('loader');
                    const d = pendingApproveLoanDate!;
                    const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00.000Z`;
                    await loanManagementService.approveLoan(Number(loanId), { startDate: localDate });
                    setIsApproveSuccessOpen(true);
                }
                catch (err) {
                    console.log('approveLoan err: ', err);
                }
                finally {
                    document.querySelector('.loader-container')?.classList.remove('loader');
                }
            }}
            onNo={() => setIsApproveConfirmOpen(false)}
        />
        <ConfirmModal2
            icon={questionIcon}
            title=""
            message={<strong>Are you sure you want to save this as draft?</strong>}
            buttonYesText="Save"
            isOpen={isSaveConfirmOpen}
            onYes={() => { setIsSaveConfirmOpen(false); handleSaveAsDraft(); }}
            onNo={() => setIsSaveConfirmOpen(false)}
        />
        <ConfirmModal2
            icon={warningIcon}
            title="Decline Application"
            message={<>Are you sure you want to decline &nbsp; <strong>{loanId} loan application</strong>?</>}
            buttonYesText="Decline"
            isOpen={isDeclineConfirmOpen}
            onYes={() => { setIsDeclineConfirmOpen(false); handleDecline(); }}
            onNo={() => setIsDeclineConfirmOpen(false)}
        />
        <ApproveLoanModal
            isOpen={isApproveModalOpen}
            onConfirm={(startLoanDate: Date) => {
                setIsApproveModalOpen(false);
                setPendingApproveLoanDate(startLoanDate);
                setIsApproveConfirmOpen(true);
            }}
            onCancel={() => setIsApproveModalOpen(false)}
        />
        </>
    );
}

export default PreviewLoanApplicationForm;
